import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const money = (v) => (v == null ? "—" : `£${Number(v).toFixed(2)}`);
export const d = (v) => (v ? new Date(v.endsWith("Z") || v.includes("+") ? v : `${v}Z`).toLocaleDateString("en-GB") : "—");
export const SiteTag = ({ site }) => (
  <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${site === "new" ? "bg-emerald-100 text-emerald-800" : site === "test" ? "bg-fuchsia-100 text-fuchsia-800" : "bg-slate-200 text-slate-700"}`}>{site === "new" ? "New site" : site === "test" ? "Test" : "Old site"}</span>
);
const PLAN = { monthly: "Monthly", biannual: "6-monthly", annual: "Yearly" };
const PAY_TONE = { completed: "bg-emerald-100 text-emerald-800", processing: "bg-sky-100 text-sky-800", failed: "bg-rose-100 text-rose-800", refunded: "bg-amber-100 text-amber-800", pending: "bg-slate-100 text-slate-700" };

function Dispatch({ x }) {
  const s = x.dispatch || {};
  if (s.state === "job_created") return <span className="text-xs font-semibold text-violet-700">Job {s.job_legacy_id ? <Link className="underline" to={`/tasks/${s.job_legacy_id}`}>{s.job_legacy_id}</Link> : "created"}{s.refund_review && <span className="ml-1 rounded bg-rose-100 px-1 text-rose-700">refund – review</span>}</span>;
  if (s.state === "due_waiting") return <span className="text-xs font-semibold text-amber-700" title={s.reason}>Due – waiting<div className="font-normal">{s.reason}</div></span>;
  if (s.state === "historical") return <span className="text-xs text-slate-600">Historical milestone · {s.recon}</span>;
  if (s.state === "no_dispatch") return <span className="text-xs text-slate-400">No dispatch</span>;
  return <span className="text-xs text-slate-400">Not counted</span>;
}

function Filters({ f, setF }) {
  const sel = (k, opts) => (
    <select className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value, page: 1 })} data-testid={`subpay-filter-${k}`}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  return (
    <div className="flex flex-wrap gap-2">
      {sel("site", [["", "Both websites"], ["old", "Old site"], ["new", "New site"], ["test", "Test data"]])}
      {sel("status", [["", "All payments"], ["successful", "Successful only"], ["completed", "Completed"], ["processing", "Processing"], ["failed", "Failed"], ["refunded", "Refunded"], ["pending", "Pending"]])}
      {sel("dispatch", [["", "Any dispatch"], ["due", "Dispatch due"], ["job_created", "Job created"], ["due_waiting", "Due – waiting"], ["historical", "Historical milestone"], ["no_dispatch", "No dispatch"]])}
      <input className="h-9 w-56 rounded-md border border-slate-200 px-2 text-sm" placeholder="Customer, email, sub or order #" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value, page: 1 })} data-testid="subpay-search" />
    </div>
  );
}

export function SubscriptionPaymentsPanel() {
  const [f, setF] = useState({ site: "", status: "", dispatch: "", q: "", page: 1 });
  const [data, setData] = useState(null);
  const [sync, setSync] = useState({});
  const load = useCallback(() => { api.get("/subscription-payments", { params: f }).then((r) => setData(r.data)).catch(() => setData({ rows: [], total: 0 })); }, [f]);
  const loadSync = useCallback(() => api.get("/subscription-payments/sync-status").then((r) => { setSync(r.data); return r.data; }).catch(() => ({})), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSync(); }, [loadSync]);
  const start = async () => {
    try { await api.post("/subscription-payments/sync", { confirm: true }); toast.success("Reading both websites… this takes about 2 minutes"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start"); return; }
    const t = setInterval(async () => { const s = await loadSync(); if (!s.running) { clearInterval(t); load(); toast.success(`Synced ${s.done} subscriptions`); } }, 6000);
  };
  return (
    <div className="space-y-3" data-testid="subpay-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Filters f={f} setF={setF} />
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {sync.finished_at && <span data-testid="subpay-last-sync">Last read {new Date(sync.finished_at).toLocaleString("en-GB")}</span>}
          <Button size="sm" variant="outline" disabled={sync.running} onClick={start} data-testid="subpay-sync-btn"><RefreshCw className={`w-4 h-4 ${sync.running ? "animate-spin" : ""}`} /> {sync.running ? `Reading… ${sync.done || 0}` : "Read payments from websites"}</Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="subpay-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500"><tr>
            {["Paid", "Customer", "Subscription", "Amount", "Status", "Payment #", "Plan", "Next payment", "Dispatch"].map((h) => <th key={h} className="p-2.5 whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {data && data.rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500" data-testid="subpay-empty">No payments yet — click Read payments from websites.</td></tr>}
            {(data?.rows || []).map((x) => (
              <tr key={`${x.site}-${x.wc_order_id}`} className="border-t border-slate-100 align-top" data-testid={`subpay-row-${x.site}-${x.wc_order_id}`}>
                <td className="p-2.5 whitespace-nowrap">{d(x.paid_at || x.created_at)}</td>
                <td className="p-2.5"><div className="font-semibold">{x.customer || x.email}</div><SiteTag site={x.site} /></td>
                <td className="p-2.5 text-xs">#{x.wc_sub_id}<div className="text-slate-500">order #{x.wc_order_id} · {x.kind}</div></td>
                <td className="p-2.5 whitespace-nowrap">{money(x.amount)}{x.refunded > 0 && <div className="text-xs text-amber-700">−{money(x.refunded)} refunded</div>}</td>
                <td className="p-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PAY_TONE[x.order_status] || "bg-slate-100 text-slate-700"}`}>{x.order_status}</span></td>
                <td className="p-2.5 font-bold" data-testid={`subpay-no-${x.site}-${x.wc_order_id}`}>{x.payment_no ?? "—"}</td>
                <td className="p-2.5 text-xs">{PLAN[x.plan] || x.plan}</td>
                <td className="p-2.5 whitespace-nowrap text-xs">{d(x.next_payment)}</td>
                <td className="p-2.5"><Dispatch x={x} /></td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {data && data.total > 50 && (
        <div className="flex items-center gap-2 text-sm">
          <Button size="sm" variant="outline" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} data-testid="subpay-prev">Previous</Button>
          <span>Page {f.page} of {Math.ceil(data.total / 50)} · {data.total} payments</span>
          <Button size="sm" variant="outline" disabled={f.page * 50 >= data.total} onClick={() => setF({ ...f, page: f.page + 1 })} data-testid="subpay-next">Next</Button>
        </div>)}
    </div>
  );
}
